/**
 * One control for every duration a reader types: a whole number and a unit, stored in
 * seconds. The shadcn registry has no duration primitive, so this composes `Input` and
 * `Select`. A month is 30 days, as `api` counts one.
 *
 * `onChange` receives the seconds the fields spell, or `null` while the number is empty
 * or not a whole number; `durationInRange` is the one bound check a caller and this
 * control's own error line share.
 */
import { useEffect, useId, useState, type ReactNode } from 'react';

import { Input } from '../../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { durationCatalogue, type DurationUnit } from './durationMessages';

export type { DurationUnit } from './durationMessages';

export const UNIT_SECONDS: Record<DurationUnit, number> = {
  minutes: 60,
  hours: 3600,
  days: 86400,
  weeks: 7 * 86400,
  months: 30 * 86400,
};

export const DEFAULT_UNITS: DurationUnit[] = ['hours', 'days', 'weeks', 'months'];

/** The largest unit that divides `seconds` evenly, so a stored value reads back as typed. */
export function unitFor(seconds: number, units: DurationUnit[] = DEFAULT_UNITS): DurationUnit {
  const fitting = [...units]
    .reverse()
    .find((unit) => seconds > 0 && seconds % UNIT_SECONDS[unit] === 0);
  return fitting ?? units[0]!;
}

/** "30 dias", "6 horas": a duration spelled in the largest unit that divides it. */
export function formatDuration(seconds: number, units: DurationUnit[] = DEFAULT_UNITS): string {
  const unit = unitFor(seconds, units);
  const amount = seconds / UNIT_SECONDS[unit];
  return durationCatalogue.plural(`unit.${unit}.other`, amount, { count: amount });
}

export function durationInRange(seconds: number | null, min: number, max?: number): boolean {
  return seconds !== null && seconds >= min && (max === undefined || seconds <= max);
}

function parse(text: string, unit: DurationUnit): number | null {
  if (text.trim() === '') return null;
  const amount = Number(text);
  return Number.isInteger(amount) && amount >= 0 ? amount * UNIT_SECONDS[unit] : null;
}

export function DurationInput(props: {
  id?: string;
  seconds: number | null;
  onChange: (seconds: number | null) => void;
  minSeconds: number;
  maxSeconds?: number;
  units?: DurationUnit[];
  disabled?: boolean;
  'aria-label'?: string;
}): ReactNode {
  const units = props.units ?? DEFAULT_UNITS;
  const [unit, setUnit] = useState<DurationUnit>(() =>
    props.seconds === null ? units[0]! : unitFor(props.seconds, units),
  );
  const [text, setText] = useState(() =>
    props.seconds === null ? '' : String(props.seconds / UNIT_SECONDS[unit]),
  );
  const errorId = useId();

  // A parent that resets its value (discard, a refetch) re-spells the fields; the
  // parent echoing back what the fields just emitted changes nothing.
  useEffect(() => {
    if (props.seconds === parse(text, unit)) return;
    if (props.seconds === null) {
      setText('');
      return;
    }
    const next = unitFor(props.seconds, units);
    setUnit(next);
    setText(String(props.seconds / UNIT_SECONDS[next]));
  }, [props.seconds]);

  const parsed = parse(text, unit);
  const invalid =
    text.trim() !== '' && !durationInRange(parsed, props.minSeconds, props.maxSeconds);
  const dt = durationCatalogue.t;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-2">
        <Input
          id={props.id}
          type="number"
          inputMode="numeric"
          min={0}
          step={1}
          className="min-w-0 flex-1"
          value={text}
          disabled={props.disabled}
          aria-label={props['aria-label']}
          aria-invalid={invalid}
          aria-describedby={invalid ? errorId : undefined}
          onChange={(event) => {
            setText(event.target.value);
            props.onChange(parse(event.target.value, unit));
          }}
        />
        <Select
          value={unit}
          disabled={props.disabled}
          onValueChange={(value) => {
            const next = value as DurationUnit;
            setUnit(next);
            props.onChange(parse(text, next));
          }}
        >
          <SelectTrigger className="w-32 shrink-0" aria-label={dt('unit.label')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {units.map((option) => (
              <SelectItem key={option} value={option}>
                {dt(`unit.${option}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {invalid && (
        <p id={errorId} role="alert" className="text-caption text-destructive">
          {props.maxSeconds === undefined
            ? dt('error.min', { min: formatDuration(props.minSeconds, units) })
            : dt('error.range', {
                min: formatDuration(props.minSeconds, units),
                max: formatDuration(props.maxSeconds, units),
              })}
        </p>
      )}
    </div>
  );
}
